// @defect https://linear.app/ecoplanet/issue/EC-5003/add-overall-group-view-and-all-possible-energy-types-in-series
import { ALL_LOCATIONS, useLocationsContext } from '@/src/contexts/LocationsContext'
import { SeriesTypeEnum } from '@/src/features/analytics/enums/seriesType.enum'
import { useIsAtlasEnabled } from '@/src/shared/feature-flag/feature-flag.hooks'
import type { ISelectOption } from '@/src/types/selectOption.interface'
import {
  type TimeSeriesOptionGroup,
  type TimeSeriesReference,
  type TimeSeriesSelectOption,
  getTimeSeriesDisplayGroup,
  getTimeSeriesSelectionKey,
} from '@/src/types/timeSeriesSelection.interface'
import { ALL_BUILDINGS_ID, ALL_ENERGY_TYPES_ID } from '@/src/utils/constants'
import { getSeriesTypeLabel } from '@/src/utils/seriesType.utils'
import { useDisclosure } from '@chakra-ui/react'
import { t } from 'i18next'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { ITimeSeriesSelectedOptions } from './types/timeSeriesSelectedOptions.interface'
import { useAtlasTimeSeriesOptions } from './useAtlasTimeSeriesOptions.hook'
import { type ITimeSeriesData, useTimeSeriesSelectData } from './useTimeSeriesSelectData.hook'

interface IOptionState {
  location: ISelectOption[]
  building: ISelectOption[]
  seriesType: ISelectOption[]
  timeSeries: TimeSeriesSelectOption[]
}

const matchesSparseSearch = (term: string, value: string): boolean => {
  const search = term.replace(/\s+/g, '').toLocaleLowerCase()
  const target = value.replace(/\s+/g, '').toLocaleLowerCase()
  return target.includes(search)
}

const constructLocationOptions = (locations: Array<{ locationPgId: number; name?: string | null }>, searchTerm: string): ISelectOption[] =>
  locations
    .map(location => ({ value: location.locationPgId, label: location.name ?? '' }))
    .filter(option => matchesSparseSearch(searchTerm, option.label))

const SERIES_TYPE_ORDER: SeriesTypeEnum[] = [
  SeriesTypeEnum.electricity,
  SeriesTypeEnum.gas,
  SeriesTypeEnum.fluidGas,
  SeriesTypeEnum.fuels,
  SeriesTypeEnum.water,
  SeriesTypeEnum.pressurizedAir,
  SeriesTypeEnum.heating,
  SeriesTypeEnum.virtualMeter,
  SeriesTypeEnum.temperature,
  SeriesTypeEnum.special,
  SeriesTypeEnum.custom,
]

const getSeriesTypesFromTimeSeries = (timeSeries: ITimeSeriesData['timeSeries']): SeriesTypeEnum[] => {
  const seriesTypeValues = timeSeries.reduce<SeriesTypeEnum[]>((types, item) => {
    if (item.seriesType && !types.includes(item.seriesType)) types.push(item.seriesType)
    return types
  }, [])

  if (timeSeries.some(item => item.isVirtual) && !seriesTypeValues.includes(SeriesTypeEnum.virtualMeter)) {
    seriesTypeValues.push(SeriesTypeEnum.virtualMeter)
  }

  return seriesTypeValues.sort((a, b) => SERIES_TYPE_ORDER.indexOf(a) - SERIES_TYPE_ORDER.indexOf(b))
}

const constructSeriesTypeOptions = (seriesTypes: SeriesTypeEnum[], searchTerm: string): ISelectOption[] => {
  const options = seriesTypes
    .map(seriesType => ({ value: seriesType, label: getSeriesTypeLabel(seriesType) }))
    .filter(option => matchesSparseSearch(searchTerm, option.label))

  if (options.length <= 1) return options
  return [{ value: ALL_ENERGY_TYPES_ID, label: t('common.all_types') }, ...options]
}

const getLocationFilter = (locationId: string | number, locations: Array<{ locationPgId: number }>) => {
  if (locationId === ALL_LOCATIONS) {
    const allowedIds = new Set(locations.map(location => location.locationPgId))
    return (item: ITimeSeriesData['timeSeries'][number]): boolean => allowedIds.has(item.locationId)
  }

  return (item: ITimeSeriesData['timeSeries'][number]): boolean => item.locationId === locationId
}

const getSeriesTypeFilter = (seriesType: string | number) => {
  if (!seriesType || seriesType === ALL_ENERGY_TYPES_ID) return () => true
  if (seriesType === SeriesTypeEnum.virtualMeter) {
    return (item: ITimeSeriesData['timeSeries'][number]): boolean => item.isVirtual === true
  }
  return (item: ITimeSeriesData['timeSeries'][number]): boolean => item.seriesType === seriesType && item.isVirtual !== true
}

const getFallbackGroup = (item: ITimeSeriesData['timeSeries'][number]): TimeSeriesOptionGroup => {
  if (
    item.seriesType === SeriesTypeEnum.custom ||
    item.seriesType === SeriesTypeEnum.special ||
    item.seriesType === SeriesTypeEnum.temperature
  ) {
    return 'other'
  }
  if (item.deviceType === 'generation') return 'onsite_generation'
  if (item.isDefault) return 'grid_import'
  return 'submeter'
}

const appendLocationLabel = (label: string, locationLabel?: string): string => {
  if (!locationLabel || label.includes(locationLabel)) return label
  return `${label} (${locationLabel})`
}

const getCanonicalKey = (locationId: number, referenceId: string): string => `${locationId}:${referenceId}`

const toTimeSeriesOption = (item: ITimeSeriesData['timeSeries'][number], labelOverride?: string): TimeSeriesSelectOption => ({
  value: item.key,
  label: labelOverride || item.label || item.name || item.id,
  selection: {
    reference: item.reference,
    locationId: item.locationId,
    metadata: {
      label: labelOverride || item.label || item.name || item.id,
      energyType: item.energyType,
      unit: item.unit,
      resolution: item.resolution,
      minDate: item.minDate,
      maxDate: item.maxDate,
      dataQualityTimeSeriesIds: item.dataQualityTimeSeriesIds,
      selectable: item.selectable,
      disabledReason: item.disabledReason,
      formulaVersion: item.formulaVersion,
    },
  },
  group: item.group,
  isVirtual: item.isVirtual,
  source: item.source,
})

const matchesTimeSeriesSearch = (option: TimeSeriesSelectOption, searchTerm: string): boolean => {
  if (matchesSparseSearch(searchTerm, option.label)) return true
  if (!option.group) return false
  return matchesSparseSearch(searchTerm, t(`enpi.seu_selector.groups.${getTimeSeriesDisplayGroup(option.group)}`))
}

const compareTemperatureSeries = (a: ITimeSeriesData['timeSeries'][number], b: ITimeSeriesData['timeSeries'][number]): number => {
  if (a.id === 'temperature_celsius' && b.id === 'temperature_celsius') return 0
  if (a.id === 'temperature_celsius') return -1
  if (b.id === 'temperature_celsius') return 1
  return 0
}

const constructTimeSeriesOptions = (
  filteredTimeSeries: ITimeSeriesData['timeSeries'],
  locationId: string | number,
  searchTerm: string,
  showTemperatureLocationContext: boolean
): TimeSeriesSelectOption[] => {
  const regularOptions = filteredTimeSeries
    .filter(item => item.seriesType !== SeriesTypeEnum.temperature)
    .map(item => toTimeSeriesOption(item))
    .filter(option => matchesTimeSeriesSearch(option, searchTerm))
  const temperatureSeries = filteredTimeSeries.filter(item => item.seriesType === SeriesTypeEnum.temperature).sort(compareTemperatureSeries)

  if (locationId === ALL_LOCATIONS) {
    const existingLabels = new Set(regularOptions.map(option => option.label))
    for (const item of temperatureSeries) {
      const option = toTimeSeriesOption(item, showTemperatureLocationContext ? undefined : item.name)
      if (!existingLabels.has(option.label) && matchesTimeSeriesSearch(option, searchTerm)) {
        regularOptions.push(option)
        existingLabels.add(option.label)
      }
    }
    return regularOptions
  }

  for (const item of temperatureSeries) {
    if (item.locationId !== locationId) continue
    const option = toTimeSeriesOption(item, showTemperatureLocationContext ? undefined : item.name)
    if (!regularOptions.some(candidate => candidate.value === option.value) && matchesTimeSeriesSearch(option, searchTerm)) {
      regularOptions.push(option)
    }
  }
  return regularOptions
}

interface IUseTimeSeriesSelectParams {
  initialSelectedTimeseriesId?: string | null
  initialSelectedTimeSeriesReference?: TimeSeriesReference
  initialSelectedLocationId?: number
  includeLocationTotals?: boolean
  excludeTimeSeriesIds?: string[]
  onSelect: (selectedOptions: ITimeSeriesSelectedOptions) => void
  excludeSeriesTypes?: string[]
  includeSourceGranularityMinutes?: number[]
  includeLocationTotalSourceGranularityMinutes?: number[]
}

export const useTimeSeriesSelect = ({
  initialSelectedTimeseriesId,
  initialSelectedTimeSeriesReference,
  initialSelectedLocationId,
  includeLocationTotals = false,
  onSelect,
  excludeTimeSeriesIds,
  excludeSeriesTypes,
  includeSourceGranularityMinutes,
  includeLocationTotalSourceGranularityMinutes,
}: IUseTimeSeriesSelectParams) => {
  const locationsContext = useLocationsContext()
  const isAtlasEnabled = useIsAtlasEnabled() === true
  const data = useTimeSeriesSelectData({ excludeTimeSeriesIds, excludeSeriesTypes, includeSourceGranularityMinutes })
  const createTimeSeriesModal = useDisclosure()
  const [options, setOptions] = useState<IOptionState>({ location: [], building: [], seriesType: [], timeSeries: [] })
  const [selectedOptions, setSelectedOptions] = useState<ITimeSeriesSelectedOptions>(() => ({
    location: {
      value: initialSelectedLocationId ?? ALL_LOCATIONS,
      label:
        initialSelectedLocationId == null
          ? t('common.all_locations')
          : locationsContext.allLocations.find(location => location.locationPgId === initialSelectedLocationId)?.name ?? '',
    },
    building: { value: ALL_BUILDINGS_ID, label: t('common.all_buildings') },
    seriesType: { value: ALL_ENERGY_TYPES_ID, label: t('common.all_types') },
    timeSeries: { value: '', label: '' },
  }))
  const [activeSearchBoxId, setActiveSearchBoxId] = useState<number>(-1)
  const [search, setSearch] = useState({ location: '', building: '', seriesType: '', timeSeries: '' })
  const hasInitialized = useRef(false)
  const hasUserSelected = useRef(false)
  const hasSeparateLocationTotalGranularityFilter = includeLocationTotals && includeLocationTotalSourceGranularityMinutes !== undefined
  const locationTotalGranularityFilterKey =
    (includeLocationTotalSourceGranularityMinutes ?? includeSourceGranularityMinutes)?.join(',') ?? ''

  const locationsFromTimeSeries = useMemo(() => {
    const locationSeries = isAtlasEnabled && !hasSeparateLocationTotalGranularityFilter ? data.timeSeries : data.allTimeSeries
    const locationIds = new Set(locationSeries.map(item => item.locationId))
    return locationsContext.allLocations.filter(location => location.id === 'allLocations' || locationIds.has(location.locationPgId))
  }, [data.allTimeSeries, data.timeSeries, hasSeparateLocationTotalGranularityFilter, isAtlasEnabled, locationsContext.allLocations])
  const concreteLocationIds = useMemo(
    () => locationsFromTimeSeries.map(location => location.locationPgId).filter(locationId => locationId > 0),
    [locationsFromTimeSeries]
  )
  const atlasOptionsQuery = useAtlasTimeSeriesOptions(Number(selectedOptions.location.value), concreteLocationIds, isAtlasEnabled)
  const atlasOptions = atlasOptionsQuery.data
  const allowedLocationTotalGranularityMinutes = useMemo(
    () => (locationTotalGranularityFilterKey ? new Set(locationTotalGranularityFilterKey.split(',').map(Number)) : null),
    [locationTotalGranularityFilterKey]
  )

  const availableTimeSeries = useMemo<ITimeSeriesData['timeSeries']>(() => {
    if (!isAtlasEnabled) return data.timeSeries

    const canonicalOptions = atlasOptions?.options ?? []
    const canonicalByKey = new Map(
      canonicalOptions
        .filter(option => option.group !== 'overall_consumption')
        .map(option => [getCanonicalKey(option.locationId, option.reference.id), option])
    )
    const locationNames = new Map<number, string>(
      locationsContext.allLocations.map(location => [Number(location.locationPgId), String(location.name ?? '')] as const)
    )
    const showLocationInLabel = selectedOptions.location.value === ALL_LOCATIONS
    const showEnergyTypeInLocationTotalLabel = selectedOptions.seriesType.value === ALL_ENERGY_TYPES_ID
    const enriched = data.timeSeries.map(item => {
      const canonical = canonicalByKey.get(getCanonicalKey(item.locationId, item.reference.id))
      let label = item.label || item.name || item.id
      if (showLocationInLabel) label = appendLocationLabel(label, locationNames.get(item.locationId))
      return { ...item, label, group: canonical?.group ?? getFallbackGroup(item) }
    })

    if (!includeLocationTotals) return enriched

    const excludedIds = new Set(excludeTimeSeriesIds ?? [])
    for (const option of canonicalOptions.filter(option => option.group === 'overall_consumption')) {
      if (excludedIds.has(option.reference.id)) continue
      if (allowedLocationTotalGranularityMinutes && !allowedLocationTotalGranularityMinutes.has(option.resolution.minutes)) {
        continue
      }
      let label = t('enpi.seu_selector.overall_consumption')
      if (showEnergyTypeInLocationTotalLabel) {
        label = `${label} (${getSeriesTypeLabel(option.energyType as SeriesTypeEnum)})`
      }
      if (showLocationInLabel) label = appendLocationLabel(label, locationNames.get(option.locationId))
      enriched.push({
        id: option.reference.id,
        name: label,
        locationId: option.locationId,
        seriesType: option.energyType as SeriesTypeEnum,
        label,
        key: getTimeSeriesSelectionKey(option.reference, option.locationId),
        reference: option.reference,
        energyType: option.energyType,
        unit: option.unit,
        resolution: option.resolution,
        minDate: option.minDate,
        maxDate: option.maxDate,
        dataQualityTimeSeriesIds: option.dataQualityTimeSeriesIds,
        group: option.group,
        selectable: option.selectable,
        disabledReason: option.disabledReason,
        formulaVersion: option.formulaVersion,
      })
    }
    return enriched
  }, [
    atlasOptions?.options,
    data.timeSeries,
    excludeTimeSeriesIds,
    includeLocationTotals,
    allowedLocationTotalGranularityMinutes,
    isAtlasEnabled,
    locationsContext.allLocations,
    selectedOptions.location.value,
    selectedOptions.seriesType.value,
  ])

  const getLocationLabel = (locationId: string | number): string => {
    if (locationId === ALL_LOCATIONS) return t('common.all_locations')
    return locationsFromTimeSeries.find(location => location.locationPgId === locationId)?.name ?? ''
  }

  const getFilteredTimeSeries = (locationId: string | number, seriesType: string | number) => {
    const locationFilter = getLocationFilter(locationId, locationsContext.allLocations)
    const seriesTypeFilter = getSeriesTypeFilter(seriesType)
    return availableTimeSeries.filter(item => locationFilter(item) && seriesTypeFilter(item))
  }

  const getOptionsForLocation = (locationId: string | number) => {
    const locationFilter = getLocationFilter(locationId, locationsContext.allLocations)
    const filteredTimeSeries = availableTimeSeries.filter(locationFilter)
    const seriesTypeOptions = constructSeriesTypeOptions(getSeriesTypesFromTimeSeries(filteredTimeSeries), search.seriesType)
    const selectedSeriesType = seriesTypeOptions[0]?.value ?? ALL_ENERGY_TYPES_ID
    return {
      seriesTypeOptions,
      selectedSeriesType,
      timeSeriesOptions: constructTimeSeriesOptions(
        getFilteredTimeSeries(locationId, selectedSeriesType),
        locationId,
        search.timeSeries,
        isAtlasEnabled
      ),
    }
  }

  const getSelectedTimeSeriesOption = (selectionKey: string): TimeSeriesSelectOption | undefined => {
    const item = availableTimeSeries.find(candidate => candidate.key === selectionKey)
    return item ? toTimeSeriesOption(item) : undefined
  }

  const findInitialTimeSeries = (): ITimeSeriesData['timeSeries'][number] | undefined => {
    let reference = initialSelectedTimeSeriesReference
    if (!reference && initialSelectedTimeseriesId) reference = { id: initialSelectedTimeseriesId, type: '' }
    if (!reference?.id) return undefined

    const matchingSeries = availableTimeSeries.filter(item => {
      if (item.reference.id !== reference.id) return false
      if (reference.type && item.reference.type !== reference.type) return false
      return true
    })
    if (!initialSelectedLocationId) return matchingSeries[0]
    return matchingSeries.find(item => item.locationId === initialSelectedLocationId) ?? matchingSeries[0]
  }

  useEffect(() => {
    if (availableTimeSeries.length === 0 || locationsFromTimeSeries.length === 0) return

    const initialReference =
      initialSelectedTimeSeriesReference ?? (initialSelectedTimeseriesId ? { id: initialSelectedTimeseriesId, type: '' } : undefined)
    const initialSeriesIsAvailable = initialReference
      ? availableTimeSeries.some(
          item => item.reference.id === initialReference.id && (!initialReference.type || item.reference.type === initialReference.type)
        )
      : false
    if (isAtlasEnabled && initialReference && !initialSeriesIsAvailable && (atlasOptionsQuery.isPending || atlasOptionsQuery.isFetching))
      return

    const locationOptions = constructLocationOptions(locationsFromTimeSeries, search.location)
    const buildingOptions = [{ value: ALL_BUILDINGS_ID, label: t('common.all_buildings') }]
    let locationId = selectedOptions.location.value
    const initialLocationIsAvailable =
      initialSelectedLocationId != null && locationsFromTimeSeries.some(location => location.locationPgId === initialSelectedLocationId)
    if (!hasInitialized.current && initialLocationIsAvailable) {
      locationId = initialSelectedLocationId
    } else if (!locationsFromTimeSeries.some(location => location.locationPgId === locationId)) {
      locationId = locationOptions[0]?.value ?? ALL_LOCATIONS
    }

    const locationSelection = getOptionsForLocation(locationId)
    let selectedSeriesType = selectedOptions.seriesType.value
    if (!locationSelection.seriesTypeOptions.some(option => option.value === selectedSeriesType)) {
      selectedSeriesType = locationSelection.selectedSeriesType
    }
    const timeSeriesOptions = constructTimeSeriesOptions(
      getFilteredTimeSeries(locationId, selectedSeriesType),
      locationId,
      search.timeSeries,
      isAtlasEnabled
    )

    const initialTimeSeries = findInitialTimeSeries()
    const selectedReference = selectedOptions.timeSeries.selection?.reference
    const initialSelectionIsSelected =
      Boolean(initialReference) &&
      selectedReference?.id === initialReference?.id &&
      (!initialReference?.type || selectedReference?.type === initialReference.type)
    const shouldApplyInitialSelection = Boolean(initialTimeSeries) && !initialSelectionIsSelected && !hasUserSelected.current
    let selectedTimeSeries = timeSeriesOptions.find(option => option.value === selectedOptions.timeSeries.value)
    if (!hasInitialized.current || shouldApplyInitialSelection) {
      selectedTimeSeries = initialTimeSeries ? toTimeSeriesOption(initialTimeSeries) : timeSeriesOptions[0]
    }
    if (!selectedTimeSeries) selectedTimeSeries = timeSeriesOptions[0]

    const nextSelectedOptions: ITimeSeriesSelectedOptions = {
      location: { value: locationId, label: getLocationLabel(locationId) },
      building: { value: ALL_BUILDINGS_ID, label: t('common.all_buildings') },
      seriesType: {
        value: selectedSeriesType,
        label:
          selectedSeriesType === ALL_ENERGY_TYPES_ID ? t('common.all_types') : getSeriesTypeLabel(selectedSeriesType as SeriesTypeEnum),
      },
      timeSeries: selectedTimeSeries ?? { value: '', label: '' },
    }

    hasInitialized.current = true
    setOptions({
      location: locationOptions,
      building: buildingOptions,
      seriesType: locationSelection.seriesTypeOptions,
      timeSeries: timeSeriesOptions,
    })
    setSelectedOptions(nextSelectedOptions)
    onSelect(nextSelectedOptions)
    // Rebuild only when the underlying option source changes. Search and user selection
    // are handled explicitly below so this effect cannot reset an in-progress choice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableTimeSeries, locationsFromTimeSeries, atlasOptionsQuery.isFetching, atlasOptionsQuery.isPending])

  const handleSelect = (list: 'location' | 'building' | 'seriesType' | 'timeSeries', value: string | number, title = '') => {
    hasUserSelected.current = true
    if (list === 'location') {
      const next = getOptionsForLocation(value)
      const timeSeries = next.timeSeriesOptions[0] ?? { value: '', label: '' }
      const nextSelectedOptions: ITimeSeriesSelectedOptions = {
        ...selectedOptions,
        location: { value, label: getLocationLabel(value) },
        seriesType: {
          value: next.selectedSeriesType,
          label:
            next.selectedSeriesType === ALL_ENERGY_TYPES_ID
              ? t('common.all_types')
              : getSeriesTypeLabel(next.selectedSeriesType as SeriesTypeEnum),
        },
        timeSeries,
      }
      setOptions(previous => ({ ...previous, seriesType: next.seriesTypeOptions, timeSeries: next.timeSeriesOptions }))
      setSelectedOptions(nextSelectedOptions)
      onSelect(nextSelectedOptions)
      return
    }

    if (list === 'seriesType') {
      const timeSeriesOptions = constructTimeSeriesOptions(
        getFilteredTimeSeries(selectedOptions.location.value, value),
        selectedOptions.location.value,
        search.timeSeries,
        isAtlasEnabled
      )
      const timeSeries = timeSeriesOptions[0] ?? { value: '', label: '' }
      const nextSelectedOptions: ITimeSeriesSelectedOptions = {
        ...selectedOptions,
        seriesType: {
          value,
          label: value === ALL_ENERGY_TYPES_ID ? t('common.all_types') : getSeriesTypeLabel(value as SeriesTypeEnum),
        },
        timeSeries,
      }
      setOptions(previous => ({ ...previous, timeSeries: timeSeriesOptions }))
      setSelectedOptions(nextSelectedOptions)
      onSelect(nextSelectedOptions)
      return
    }

    if (list !== 'timeSeries') return
    let timeSeries = getSelectedTimeSeriesOption(String(value))
    if (!timeSeries && title) {
      const locationId = Number(selectedOptions.location.value)
      const reference = { id: String(value), type: SeriesTypeEnum.custom }
      timeSeries = {
        value: getTimeSeriesSelectionKey(reference, locationId),
        label: title,
        selection: {
          reference,
          locationId,
          metadata: {
            label: title,
            energyType: SeriesTypeEnum.custom,
            unit: '',
            resolution: { enum: '', label: '', minutes: 0 },
            minDate: null,
            maxDate: null,
            dataQualityTimeSeriesIds: [reference.id],
          },
        },
        group: isAtlasEnabled ? 'other' : undefined,
      }
      setOptions(previous => ({ ...previous, timeSeries: [timeSeries!, ...previous.timeSeries] }))
    }
    if (!timeSeries) return
    const nextSelectedOptions = { ...selectedOptions, timeSeries }
    setSelectedOptions(nextSelectedOptions)
    onSelect(nextSelectedOptions)
  }

  const handleSearch = (list: 'location' | 'building' | 'seriesType' | 'timeSeries', term: string) => {
    setSearch(previous => ({ ...previous, [list]: term }))

    if (list === 'location') {
      setOptions(previous => ({ ...previous, location: constructLocationOptions(locationsFromTimeSeries, term) }))
      return
    }
    if (list === 'seriesType') {
      const locationFilter = getLocationFilter(selectedOptions.location.value, locationsContext.allLocations)
      const filtered = availableTimeSeries.filter(locationFilter)
      setOptions(previous => ({ ...previous, seriesType: constructSeriesTypeOptions(getSeriesTypesFromTimeSeries(filtered), term) }))
      return
    }
    if (list === 'timeSeries') {
      const filtered = getFilteredTimeSeries(selectedOptions.location.value, selectedOptions.seriesType.value)
      setOptions(previous => ({
        ...previous,
        timeSeries: constructTimeSeriesOptions(filtered, selectedOptions.location.value, term, isAtlasEnabled),
      }))
    }
  }

  const handleTimeSeriesSelectModalClose = (reason?: 'submit' | 'cancel', timeSeries?: { id: string; title?: string }) => {
    if (timeSeries?.id && reason === 'submit') handleSelect('timeSeries', timeSeries.id, timeSeries.title)
    createTimeSeriesModal.onClose()
  }

  return {
    options,
    selectedOptions,
    handleSelect,
    search,
    handleSearch,
    activeSearchBoxId,
    setActiveSearchBoxId,
    showLocationList: true,
    showBuildingList: false,
    showSeriesTypeList: true,
    showTimeSeriesList: true,
    createTimeSeriesModal: { ...createTimeSeriesModal, onClose: handleTimeSeriesSelectModalClose },
  }
}
